<template>
	<section id="sectors" class="section section--alt">
		<div class="container">
			<header v-reveal class="section__head">
				<h2 class="section__title">{{ t('products.detail-title') }}</h2>
			</header>

			<div
				v-reveal
				:aria-label="t('products.detail-title')"
				class="sectors__tabs"
				role="tablist"
			>
				<button
					v-for="(sector, index) in SECTORS"
					:key="sector.key"
					:ref="(element) => setTabRef(element, index)"
					:id="`tab-${sector.key}`"
					:aria-selected="String(activeSector === sector.key)"
					:aria-controls="`panel-${sector.key}`"
					:tabindex="activeSector === sector.key ? 0 : -1"
					class="sectors__tab"
					type="button"
					role="tab"
					@click="activeSector = sector.key"
					@keydown="moveTabFocus"
				>{{ t(`products.sectors.${sector.key}.tab`) }}</button>
			</div>

			<div
				v-for="sector in SECTORS"
				:key="sector.key"
				:id="`panel-${sector.key}`"
				:hidden="activeSector !== sector.key"
				:aria-labelledby="`tab-${sector.key}`"
				:style="{ '--accent': sector.color }"
				class="sectors__panel"
				role="tabpanel"
			>
				<div class="sectors__head">
					<img
						:src="assetUrl(sector.logo)"
						:alt="t(`products.sectors.${sector.key}.name`)"
						class="sectors__logo"
						loading="lazy"
					>
					<h3 class="sectors__title">
						<span class="marker">{{ t(`products.sectors.${sector.key}.title`) }}</span>
					</h3>
				</div>

				<p class="sectors__erp">
					{{ t('products.erp-lead') }}
					<span class="sectors__erp-tail">{{ t('products.erp-tail') }}</span>
				</p>

				<div class="module-grid">
					<div
						v-for="(moduleKey, index) in sector.moduleKeys"
						:key="moduleKey"
						class="module"
					>
						<img
							:src="moduleIconUrl(sector.key, index)"
							class="module__icon"
							alt=""
							loading="lazy"
						>
						<span class="module__label">
							{{ t(`products.sectors.${sector.key}.modules.${moduleKey}`) }}
						</span>
					</div>
				</div>

				<div class="partners__head">
					<h4 class="partners__title">
						<span class="marker">{{ t('products.partners-label') }}</span>
					</h4>
					<span class="partners__note">
						{{ t('products.partners-note', { count: PARTNER_COUNTS[sector.key] }) }}
					</span>
				</div>

				<div class="partners__grid">
					<div
						v-for="(logo, index) in partnerLogoUrls(sector.key)"
						:key="index"
						class="partner"
					>
						<img
							:src="logo"
							class="partner__logo"
							alt=""
							loading="lazy"
							decoding="async"
						>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { PARTNER_COUNTS, SECTORS } from '@/helpers/constants'
import { assetUrl, moduleIconUrl, partnerLogoUrls } from '@/helpers/functions'

defineOptions({ name: 'SectorsSection' })

const { t } = useI18n()

const activeSector = ref(SECTORS[0].key)

const tabRefs = ref([])

const setTabRef = (element, index) => {
	tabRefs.value[index] = element
}

// ← and → walk the tablist, mirrored while reading right-to-left
const moveTabFocus = (event) => {
	const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
	if (!step) return

	event.preventDefault()

	const isRTL = document.documentElement.dir === 'rtl'
	const direction = isRTL ? -step : step
	const current = SECTORS.findIndex((sector) => sector.key === activeSector.value)
	const next = (current + direction + SECTORS.length) % SECTORS.length

	activeSector.value = SECTORS[next].key
	if (tabRefs.value[next]) tabRefs.value[next].focus()
}
</script>

<style scoped lang="scss">
.sectors__tabs {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
	justify-content: center;
	margin-bottom: clamp(26px, 3vw, 40px);
}

.sectors__tab {
	padding: 11px 24px;
	border: 1.5px solid $color-line-strong;
	border-radius: 999px;
	background: $color-paper;
	font-size: 0.92rem;
	font-weight: 600;
	color: #4a4a4a;
	cursor: pointer;
	transition: all 0.25s $ease;

	&:hover {
		border-color: $color-ink;
		color: $color-ink;
	}

	&[aria-selected='true'] {
		background: $color-gold;
		border-color: $color-gold;
		color: $color-ink;
		box-shadow: var(--shadow-gold);
	}
}

.sectors__panel {
	padding: clamp(26px, 3.4vw, 48px);
	background: $color-paper;
	border: 1px solid $color-line;
	border-radius: $radius-lg;
	box-shadow: var(--shadow-sm);

	&[hidden] {
		display: none;
	}
}

.sectors__head {
	display: flex;
	align-items: center;
	gap: clamp(16px, 2.4vw, 30px);
	flex-wrap: wrap;
}

.sectors__logo {
	height: 54px;
	width: auto;
	object-fit: contain;
}

.sectors__title {
	font-size: clamp(1.2rem, 2.4vw, 1.65rem);
	font-weight: 800;
}

.sectors__erp {
	margin: clamp(28px, 3vw, 40px) 0 20px;
	font-size: 0.98rem;
	font-weight: 600;
	color: $color-ink;
}

.sectors__erp-tail {
	color: var(--accent, #{$color-gold-deep});
}

.module-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 290px), 1fr));
	gap: 14px;
}

.module {
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 14px 16px;
	background: $color-paper-alt;
	border: 1px solid transparent;
	border-radius: $radius-sm;
	transition: border-color 0.25s, background 0.25s, transform 0.25s $ease;

	&:hover {
		background: $color-paper;
		border-color: $color-line;
		transform: translateY(-2px);
	}
}

.module__icon {
	width: 38px;
	height: 38px;
	flex: none;
	object-fit: contain;
}

.module__label {
	font-size: 0.94rem;
	font-weight: 500;
	line-height: 1.4;
	color: $color-ink;
}

.partners__head {
	margin: clamp(34px, 4vw, 52px) 0 0;
	display: flex;
	align-items: baseline;
	gap: 14px;
	flex-wrap: wrap;
}

.partners__title {
	font-size: clamp(1.06rem, 2vw, 1.35rem);
	font-weight: 800;
}

.partners__note {
	font-size: 0.83rem;
	color: $color-muted;
}

.partners__grid {
	margin-top: 22px;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(100%, 104px), 1fr));
	gap: 10px;

	@include below($bp-mobile) {
		grid-template-columns: repeat(auto-fill, minmax(min(100%, 84px), 1fr));
		gap: 8px;
	}
}

.partner {
	display: grid;
	place-items: center;
	aspect-ratio: 1;
	padding: 12px;
	background: $color-paper;
	border: 1px solid $color-line;
	border-radius: 12px;
	transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s $ease;

	&:hover {
		border-color: $color-line-strong;
		box-shadow: var(--shadow-sm);
		transform: translateY(-2px);
	}

	&:hover .partner__logo {
		filter: none;
		opacity: 1;
	}
}

.partner__logo {
	max-width: 100%;
	max-height: 100%;
	width: auto;
	height: auto;
	// grid items default to min-height:auto, which lets tall logos stretch
	// their tile and break the row rhythm
	min-width: 0;
	min-height: 0;
	object-fit: contain;
	mix-blend-mode: multiply;
	filter: grayscale(1) contrast(1.05);
	opacity: 0.72;
	transition: filter 0.3s, opacity 0.3s;
}
</style>
